import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["inputWidth", "minHeight", "resize", "rows", "shrink", "maxHeight", "use", "inputRef", "styled", "error", "cols", "onChange", "onBlur", "onFocus", "onKeyDown", "onKeyUp", "onKeyPress"];
import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { getTextHeight } from '../../utils/MeasureText';
import { toPx } from '../../../utils/toPx';
import MeasureInput from '../MeasureInput/MeasureInput';
import { TextArea } from '../TextArea';
import { ControllableNext as Controllable } from '../Controllable/ControllableNext';
import ShareInput from '../ShareInput/ShareInput';
import { filterProps } from '../../../utils/filterProps';
import { ExpandingTextAreaContextProvider } from './ExpandingTextAreaContext';
import { jsx as _jsx } from "react/jsx-runtime";
export const ExpandingTextAreaComponent = props => {
  const {
      minHeight = 0,
      resize = 'vertical',
      shrink = false,
      maxHeight = Infinity,
      use,
      inputRef,
      styled = true,
      error = false,
      cols,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      onKeyUp,
      onKeyPress
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [height, setHeight] = useState(null);
  const [hasUpdated, setHasUpdated] = useState(false);
  const prevHeightRef = useRef(null);
  const setHeightFunc = useCallback(initial => {
    const textAreaNode = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current;
    if (!textAreaNode) return;
    const textHeight = getTextHeight(textAreaNode, null);
    let newHeight;
    if (shrink) {
      newHeight = Math.max(minHeight, textHeight);
    } else {
      const measuredHeight = initial ? 0 : textAreaNode.getBoundingClientRect().height;
      newHeight = Math.max(minHeight, textHeight, measuredHeight);
    }
    if (height !== newHeight) {
      setHeight(newHeight);
    }
  }, [height, inputRef, minHeight, shrink]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const prevHeight = prevHeightRef.current;

    // This condition prevents infinite loops
    if (prevHeight === height) {
      setHeightFunc(!hasUpdated);
      setHasUpdated(true);
    }
    prevHeightRef.current = height;
  });
  const {
    tokenProps,
    domProps
  } = filterProps(rest, {
    isTextArea: true
  });
  const heightPx = height !== null ? toPx(height) : undefined;
  const maxHeightPx = maxHeight < Infinity ? toPx(maxHeight) : undefined;
  const overflow = height && height > maxHeight ? 'auto' : 'hidden';
  return /*#__PURE__*/_jsx(ExpandingTextAreaContextProvider, {
    value: {
      height: heightPx,
      maxHeight: maxHeightPx,
      overflow
    },
    children: /*#__PURE__*/_jsx(TextArea, Object.assign({}, domProps, tokenProps, {
      resize: shrink ? false : resize,
      error: error,
      use: use,
      inputRef: inputRef,
      styled: styled,
      cols: cols,
      onChange: onChange,
      onBlur: onBlur,
      onFocus: onFocus,
      onKeyDown: onKeyDown,
      onKeyUp: onKeyUp,
      onKeyPress: onKeyPress
    }))
  });
};

// TODO: Consider removing this defaultProps if/when we decide to augment the
// Controllable component to accept defaults in it's function signature.
// see: https://git.hubteam.com/HubSpot/foundations-components/issues/5233
/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
ExpandingTextAreaComponent.defaultProps = {
  value: ''
};
export const ExpandingTextArea = Controllable(ShareInput(MeasureInput( /*#__PURE__*/memo(ExpandingTextAreaComponent))));